/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */


import {QQueryFilter} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QQueryFilter";
import RecordQueryView from "qqq/models/query/RecordQueryView";
import FilterUtils from "qqq/utils/qqq/FilterUtils";

export type QueryHistorySource = "adHoc" | "savedView" | "url";

export interface QueryHistoryEntry
{
   id: string;
   viewJson: string;
   source: QueryHistorySource;
   savedViewId: number | null;
   savedViewLabel: string | null;
   savedViewModified: boolean;
   firstUsedAt: number; // epoch millis
   lastUsedAt: number; // epoch millis
}

export interface QueryHistoryState
{
   version: 1;
   currentEntryId: string | null;
   entries: QueryHistoryEntry[]; // newest lastUsedAt first
}

export interface QueryHistoryCandidate
{
   view: RecordQueryView;
   source: QueryHistorySource;
   savedViewId: number | null;
   savedViewLabel: string | null;
   savedViewModified: boolean;
}

export const QUERY_HISTORY_MAX_ENTRIES = 50;


/*******************************************************************************
 **
 *******************************************************************************/
export function emptyQueryHistory(): QueryHistoryState
{
   return ({version: 1, currentEntryId: null, entries: []});
}


/*******************************************************************************
 ** remove incomplete criteria from the filter and every sub-filter.  Works on a
 ** filter the caller has already cloned - FilterUtils only handles one level.
 *******************************************************************************/
function stripIncompleteCriteriaRecursively(filter: any): void
{
   FilterUtils.stripAwayIncompleteCriteria(filter);
   for (const subFilter of (filter?.subFilters ?? []))
   {
      stripIncompleteCriteriaRecursively(subFilter);
   }
}


/*******************************************************************************
 ** build the canonical object that gets serialized for matching.  Only the keys
 ** that define what a query asks for are included, so paging never matters.
 *******************************************************************************/
function toCanonicalFilter(filter: any): any
{
   return ({
      booleanOperator: filter?.booleanOperator,
      criteria: (filter?.criteria ?? []).map((criteria: any) => ({
         fieldName: criteria.fieldName,
         operator: criteria.operator,
         values: criteria.values
      })),
      subFilters: (filter?.subFilters ?? []).map((subFilter: any) => toCanonicalFilter(subFilter)),
      orderBys: (filter?.orderBys ?? []).map((orderBy: any) => ({
         fieldName: orderBy.fieldName,
         isAscending: orderBy.isAscending
      }))
   });
}


/*******************************************************************************
 ** produce a string that is equal for two filters that ask for the same query,
 ** ignoring incomplete criteria, skip, and limit.  Does not mutate the input.
 *******************************************************************************/
export function normalizeQueryFilterForMatch(filter: QQueryFilter): string
{
   ///////////////////////////////////////////////////////////////////////////////
   // the JSON round trip makes a working copy for stripping, and also makes    //
   // expression class instances serialize the same as their plain-object form. //
   ///////////////////////////////////////////////////////////////////////////////
   const copy = JSON.parse(JSON.stringify(filter ?? {}));
   stripIncompleteCriteriaRecursively(copy);
   return (JSON.stringify(toCanonicalFilter(copy)));
}


/*******************************************************************************
 **
 *******************************************************************************/
function normalizedFilterOfEntry(entry: QueryHistoryEntry): string
{
   return (normalizeQueryFilterForMatch(JSON.parse(entry.viewJson).queryFilter));
}


/*******************************************************************************
 ** the current entry, if it has the candidate's filter and saved view id - else
 ** null.
 *******************************************************************************/
function findCurrentEntryForCandidate(state: QueryHistoryState, candidate: QueryHistoryCandidate): QueryHistoryEntry | null
{
   const currentEntry = state.entries.find((entry) => entry.id == state.currentEntryId);
   if (!currentEntry
      || currentEntry.savedViewId !== candidate.savedViewId
      || normalizedFilterOfEntry(currentEntry) !== normalizeQueryFilterForMatch(candidate.view.queryFilter))
   {
      return (null);
   }
   return (currentEntry);
}


/*******************************************************************************
 ** replace an entry with its updated copy, at the top of the list - for an entry
 ** that was just used, since the list is kept newest lastUsedAt first.
 *******************************************************************************/
function moveToTop(state: QueryHistoryState, original: QueryHistoryEntry, updated: QueryHistoryEntry): QueryHistoryState
{
   return ({...state, entries: [updated, ...state.entries.filter((entry) => entry !== original)]});
}


/*******************************************************************************
 ** if the current entry has the candidate's filter and saved view id, return a
 ** state with that entry's view data replaced by the candidate's - else null.
 ** Given a usedAt time, the entry was also just used (its query ran), so it
 ** takes that as its lastUsedAt and moves to the top.
 **
 ** A candidate with no saved view leaves the entry's label and modified flag as
 ** they are: those are only ever unset on such entries, except on an entry that
 ** detachFromSavedView kept as its saved view, which must keep showing as one.
 *******************************************************************************/
function amendCurrentEntry(state: QueryHistoryState, candidate: QueryHistoryCandidate, usedAt: number | null): QueryHistoryState | null
{
   const currentEntry = findCurrentEntryForCandidate(state, candidate);
   if (!currentEntry)
   {
      return (null);
   }

   let amended: QueryHistoryEntry = {...currentEntry, viewJson: JSON.stringify(candidate.view)};
   if (candidate.savedViewId != null)
   {
      amended = {...amended, savedViewModified: candidate.savedViewModified, savedViewLabel: candidate.savedViewLabel};
   }

   if (usedAt != null)
   {
      return (moveToTop(state, currentEntry, {...amended, lastUsedAt: usedAt}));
   }
   return ({...state, entries: state.entries.map((entry) => entry === currentEntry ? amended : entry)});
}


/*******************************************************************************
 ** the screen ran a query (a refresh, a page change, a reload): if it is the
 ** current entry's query, that counts as using the entry - it takes now as its
 ** lastUsedAt and moves to the top.  Any other query leaves the history alone;
 ** recordQuery is what handles a new query.
 *******************************************************************************/
export function touchCurrentEntry(state: QueryHistoryState, candidate: QueryHistoryCandidate, now: number): QueryHistoryState
{
   const currentEntry = findCurrentEntryForCandidate(state, candidate);
   if (!currentEntry)
   {
      return (state);
   }
   return (moveToTop(state, currentEntry, {...currentEntry, lastUsedAt: now}));
}


/*******************************************************************************
 ** record that the user ran a query: amend the current entry when it is the same
 ** query, else move a matching older entry to the top, else add a new entry.
 *******************************************************************************/
export function recordQuery(state: QueryHistoryState, candidate: QueryHistoryCandidate, now: number, idSupplier: () => string): QueryHistoryState
{
   const amendedState = amendCurrentEntry(state, candidate, now);
   if (amendedState)
   {
      return (amendedState);
   }

   const normalizedCandidateFilter = normalizeQueryFilterForMatch(candidate.view.queryFilter);
   const viewJson = JSON.stringify(candidate.view);

   const matchingEntry = state.entries.find((entry) =>
      entry.source === candidate.source
      && entry.savedViewId === candidate.savedViewId
      && normalizedFilterOfEntry(entry) === normalizedCandidateFilter);

   if (matchingEntry)
   {
      const updated: QueryHistoryEntry = {
         ...matchingEntry,
         viewJson: viewJson,
         savedViewLabel: candidate.savedViewLabel,
         savedViewModified: candidate.savedViewModified,
         lastUsedAt: now
      };
      return ({...state, currentEntryId: updated.id, entries: [updated, ...state.entries.filter((entry) => entry !== matchingEntry)]});
   }

   const created: QueryHistoryEntry = {
      id: idSupplier(),
      viewJson: viewJson,
      source: candidate.source,
      savedViewId: candidate.savedViewId,
      savedViewLabel: candidate.savedViewLabel,
      savedViewModified: candidate.savedViewModified,
      firstUsedAt: now,
      lastUsedAt: now
   };

   let entries = [created, ...state.entries];
   while (entries.length > QUERY_HISTORY_MAX_ENTRIES)
   {
      ///////////////////////////////////////////////////////////////////////////
      // entries are ordered by lastUsedAt, so the oldest one is the last; but //
      // search for it, rather than assume, so a hand-edited state still trims //
      // by the documented rule.  The entry just created is never the oldest.  //
      ///////////////////////////////////////////////////////////////////////////
      let oldestIndex = entries.length - 1;
      for (let i = entries.length - 1; i > 0; i--)
      {
         if (entries[i].lastUsedAt < entries[oldestIndex].lastUsedAt)
         {
            oldestIndex = i;
         }
      }
      entries = entries.filter((entry, index) => index !== oldestIndex);
   }

   return ({...state, currentEntryId: created.id, entries: entries});
}


/*******************************************************************************
 ** this is the single place where the create-or-amend rule for layout changes lives.
 *******************************************************************************/
export function applyLayoutChange(state: QueryHistoryState, candidate: QueryHistoryCandidate, now: number): QueryHistoryState
{
   return (amendCurrentEntry(state, candidate, null) ?? state);
}


/*******************************************************************************
 ** an entry's saved view is gone: clear its saved view id, so that the screen's
 ** unsaved query - the same filter - amends it from now on (including after a
 ** reload), while it keeps its saved view source and label.
 *******************************************************************************/
export function detachFromSavedView(state: QueryHistoryState, entryId: string): QueryHistoryState
{
   const entry = state.entries.find((entry) => entry.id === entryId);
   if (!entry)
   {
      return (state);
   }

   const detached: QueryHistoryEntry = {...entry, savedViewId: null};
   return ({...state, entries: state.entries.map((existing) => existing === entry ? detached : existing)});
}


/*******************************************************************************
 ** make an existing entry the current one, and the most recently used.  Given
 ** the view as restored (which can differ from the entry's, e.g. without fields
 ** the table no longer has), the entry takes that view - and if that makes it
 ** the same query as another entry, the other one goes, so there is still one
 ** entry per query, as recordQuery keeps it.
 *******************************************************************************/
export function markRestored(state: QueryHistoryState, entryId: string, now: number, restoredViewJson?: string): QueryHistoryState
{
   const restoredEntry = state.entries.find((entry) => entry.id === entryId);
   if (!restoredEntry)
   {
      return (state);
   }

   const updated: QueryHistoryEntry = {...restoredEntry, lastUsedAt: now, viewJson: restoredViewJson ?? restoredEntry.viewJson};
   let others = state.entries.filter((entry) => entry !== restoredEntry);
   if (restoredViewJson != null && restoredViewJson !== restoredEntry.viewJson)
   {
      const normalizedFilter = normalizedFilterOfEntry(updated);
      others = others.filter((entry) => !(entry.source === updated.source && entry.savedViewId === updated.savedViewId && normalizedFilterOfEntry(entry) === normalizedFilter));
   }
   return ({...state, currentEntryId: updated.id, entries: [updated, ...others]});
}


/*******************************************************************************
 ** give an empty history a first entry for a view that was already on screen.
 *******************************************************************************/
export function seedQueryHistory(state: QueryHistoryState, existingViewJson: string, savedViewId: number | null, now: number, idSupplier: () => string): QueryHistoryState
{
   if (state.entries.length > 0)
   {
      return (state);
   }

   const entry: QueryHistoryEntry = {
      id: idSupplier(),
      viewJson: existingViewJson,
      source: savedViewId == null ? "adHoc" : "savedView",
      savedViewId: savedViewId,
      savedViewLabel: null,
      savedViewModified: false,
      firstUsedAt: now,
      lastUsedAt: now
   };
   return ({...state, currentEntryId: entry.id, entries: [entry]});
}
